import { useEffect, useState } from 'react'
import { Edit2, Plus, Trash2, X } from 'lucide-react'

import {
  apiCreateVehicleCategory,
  apiDeleteVehicleCategory,
  apiGetVehicleCategories,
  apiUpdateVehicleCategory,
  type VehicleCategory,
} from '../../../../api/vehicleManagement/vehicleCategoryApi'
import { useConfirmDialog } from '../../../../context/ConfirmDialogContext'

type VehicleCategoryManagerProps = {
  refreshKey?: number
}

const emptyForm = {
  name: '',
  slug: '',
  is_active: true,
}

export const VehicleCategoryManager = ({
  refreshKey = 0,
}: VehicleCategoryManagerProps) => {
  const { confirm, showSuccess } = useConfirmDialog()
  const [categories, setCategories] = useState<VehicleCategory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] =
    useState<VehicleCategory | null>(null)

  const [formData, setFormData] = useState(emptyForm)
  const [isSaving, setIsSaving] = useState(false)

  const loadCategories = async () => {
    setIsLoading(true)
    setError(null)

    const response = await apiGetVehicleCategories()

    if (response.success) {
      setCategories(response.data || [])
    } else {
      setError(response.message)
      setCategories([])
    }

    setIsLoading(false)
  }

  useEffect(() => {
    loadCategories()
  }, [refreshKey])

  const openCreateModal = () => {
    setEditingCategory(null)
    setFormData(emptyForm)
    setIsModalOpen(true)
  }

  const openEditModal = (category: VehicleCategory) => {
    setEditingCategory(category)

    setFormData({
      name: category.name,
      slug: category.slug,
      is_active: category.is_active,
    })

    setIsModalOpen(true)
  }

  const closeModal = () => {
    if (isSaving) return

    setIsModalOpen(false)
    setEditingCategory(null)
    setFormData(emptyForm)
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Category name is required.')
      return
    }

    setIsSaving(true)
    setError(null)

    const payload = {
      name: formData.name.trim(),
      slug: formData.slug.trim() || undefined,
      is_active: formData.is_active,
    }

    const response = editingCategory
      ? await apiUpdateVehicleCategory(editingCategory.id, payload)
      : await apiCreateVehicleCategory(payload)

    if (!response.success) {
      setError(response.message)
      setIsSaving(false)
      return
    }

    await loadCategories()

    setIsSaving(false)
    closeModal()
  }

  const handleDelete = async (category: VehicleCategory) => {
    const confirmed = await confirm({
      title: 'Delete Vehicle Category',
      message: `Are you sure you want to delete "${category.name}"? This action cannot be undone.`,
      confirmText: 'Delete Category',
      cancelText: 'Cancel',
      variant: 'danger',
      icon: 'trash',
    })

    if (!confirmed) return

    const response = await apiDeleteVehicleCategory(category.id)

    if (!response.success) {
      setError(response.message)
      return
    }

    await showSuccess({
      title: 'Category Deleted',
      message: `"${category.name}" has been deleted successfully.`,
    })

    await loadCategories()
  }

  const handleToggleActive = async (category: VehicleCategory) => {
    const response = await apiUpdateVehicleCategory(category.id, {
      name: category.name,
      slug: category.slug,
      is_active: !category.is_active,
    })

    if (!response.success) {
      setError(response.message)
      return
    }

    await loadCategories()
  }

  return (
    <div className="rounded-[16px] bg-white p-[24px] shadow-sm">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-host text-[18px] font-bold text-[#1A1A1A]">
            Vehicle Categories
          </h2>

          <p className="mt-1 font-host text-[13px] text-[#757575]">
            Manage the categories available for your vehicles.
          </p>
        </div>

        <button
          type="button"
          onClick={openCreateModal}
          className="flex items-center gap-2 rounded-[8px] bg-[#334E1C] px-4 py-2.5 font-host text-[13px] font-semibold text-white transition-colors hover:bg-[#253914]"
        >
          <Plus size={16} />
          Add Category
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 font-host text-[13px] text-red-700">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="py-10 text-center font-host text-[14px] text-[#757575]">
          Loading categories...
        </div>
      ) : categories.length === 0 ? (
        <div className="rounded-[12px] border border-dashed border-[#D0D0D0] py-12 text-center">
          <p className="font-host text-[14px] text-[#757575]">
            No vehicle categories found.
          </p>

          <button
            type="button"
            onClick={openCreateModal}
            className="mt-3 font-host text-[13px] font-semibold text-[#334E1C]"
          >
            Create your first category
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[650px] border-collapse">
            <thead>
              <tr className="border-b border-[#EEEEEE] bg-[#FAFAFA]">
                <th className="px-4 py-3 text-left font-host text-[12px] font-semibold text-[#757575]">
                  Name
                </th>

                <th className="px-4 py-3 text-left font-host text-[12px] font-semibold text-[#757575]">
                  Slug
                </th>

                <th className="px-4 py-3 text-left font-host text-[12px] font-semibold text-[#757575]">
                  Status
                </th>

                <th className="px-4 py-3 text-right font-host text-[12px] font-semibold text-[#757575]">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {categories.map((category) => (
                <tr
                  key={category.id}
                  className="border-b border-[#F0F0F0] last:border-b-0"
                >
                  <td className="px-4 py-4">
                    <span className="font-host text-[14px] font-semibold text-[#1A1A1A]">
                      {category.name}
                    </span>
                  </td>

                  <td className="px-4 py-4">
                    <span className="font-host text-[13px] text-[#555555]">
                      {category.slug}
                    </span>
                  </td>

                  <td className="px-4 py-4">
                    <button
                      type="button"
                      onClick={() => handleToggleActive(category)}
                      className={`rounded-full px-3 py-1 font-host text-[11px] font-semibold ${
                        category.is_active
                          ? 'bg-[#E8F5E9] text-[#2E7D32]'
                          : 'bg-[#F5F5F5] text-[#757575]'
                      }`}
                    >
                      {category.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>

                  <td className="px-4 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openEditModal(category)}
                        className="flex size-[32px] items-center justify-center rounded-[7px] border border-[#E0E0E0] text-[#666666] hover:bg-[#F7F7F7]"
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(category)}
                        className="flex size-[32px] items-center justify-center rounded-[7px] border border-[#E0E0E0] text-red-500 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-[520px] rounded-[16px] bg-white p-[24px] shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-host text-[18px] font-bold text-[#1A1A1A]">
                {editingCategory
                  ? 'Edit Vehicle Category'
                  : 'Add Vehicle Category'}
              </h3>

              <button
                type="button"
                onClick={closeModal}
                className="text-[#757575] hover:text-[#1A1A1A]"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <label className="mb-1.5 block font-host text-[13px] font-medium text-[#1A1A1A]">
                  Category Name
                </label>

                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g. Sedan"
                  className="h-[44px] w-full rounded-[8px] border border-[#E0E0E0] px-4 font-host text-[14px] outline-none focus:border-[#FF6A1A]"
                />
              </div>

              <div>
                <label className="mb-1.5 block font-host text-[13px] font-medium text-[#1A1A1A]">
                  Slug
                </label>

                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      slug: e.target.value,
                    })
                  }
                  placeholder="e.g. sedan"
                  className="h-[44px] w-full rounded-[8px] border border-[#E0E0E0] px-4 font-host text-[14px] outline-none focus:border-[#FF6A1A]"
                />
              </div>

              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      is_active: e.target.checked,
                    })
                  }
                  className="size-4"
                />

                <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
                  Active
                </span>
              </label>
            </div>

            <div className="mt-7 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-[8px] border border-[#E0E0E0] px-5 py-2.5 font-host text-[13px] font-semibold text-[#1A1A1A]"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="rounded-[8px] bg-[#334E1C] px-5 py-2.5 font-host text-[13px] font-semibold text-white disabled:opacity-50"
              >
                {isSaving
                  ? 'Saving...'
                  : editingCategory
                    ? 'Save Changes'
                    : 'Create Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
